/**
 * One mode's card, with the two ways of playing it.
 *
 * The card stopped being a single button when co-op arrived. Playing alone and hosting for other
 * people are the same mode and different intentions, and a card that meant one of them left the
 * other somewhere else on the screen, where it read as a separate feature rather than the same game
 * with company.
 *
 * Both actions belong to a mode, so both live on its card and both take their words from what the
 * mode says about itself. Joining is the odd one out and is not here: a guest is told which mode it
 * is being taken into, so a card that offered to join would be inventing a fact.
 *
 * Hosting is offered only by the modes that can be hosted. A mode declares `party`, and a `solo`
 * one gets the one button it can honour: a join code that connects and then shows a room with
 * nobody in it is worse than no button, and the card is the only place that decision has to live.
 *
 * A press is no longer instant: a mode's code is downloaded when it is picked. The card says so on
 * the button that was pressed, rather than in a banner somewhere else, because the button is what
 * the player is looking at when they wonder whether it registered.
 *
 * A card is a title, one line, two or three chips and the buttons. No paragraph: a pitch clamped
 * to two lines and cut with an ellipsis was the one thing on the menu that read as noise, and what
 * a mode is for is learned by pressing the button.
 */

import { MODE_HOST, MODE_MOUSE_ONLY, MODE_SOLO, MODE_STARTING } from '@/text/menuText';
import { el } from '@/ui/dom';
import type { GameMode } from '@/modes/modeContract';

export interface ModeCardHandlers {
  readonly onSolo: (mode: GameMode) => void;
  readonly onHost: (mode: GameMode) => void;
}

/** Ready to be picked, or already fetching what it takes to play. */
export type ModeCardState = 'ready' | 'starting';

export interface ModeCard {
  readonly root: HTMLElement;
  setState(state: ModeCardState): void;
}

export function createModeCard(mode: GameMode, handlers: ModeCardHandlers): ModeCard {
  const root = el('section', 'mode-card');
  const solo = action(MODE_SOLO, 'mode-play', () => {
    handlers.onSolo(mode);
  });

  root.append(
    el('span', 'mode-title', mode.title),
    el('span', 'mode-tagline', mode.tagline),
    factList(mode),
    actions(solo, mode, handlers),
  );

  return {
    root,
    setState(state: ModeCardState): void {
      const starting = state === 'starting';
      root.classList.toggle('is-starting', starting);
      solo.textContent = starting ? MODE_STARTING : MODE_SOLO;
    },
  };
}

function actions(solo: HTMLButtonElement, mode: GameMode, handlers: ModeCardHandlers): HTMLElement {
  const row = el('div', 'mode-actions');
  row.append(solo);
  if (mode.party === 'coop') {
    row.append(
      action(MODE_HOST, 'mode-host', () => {
        handlers.onHost(mode);
      }),
    );
  }
  return row;
}

function action(label: string, className: string, onPress: () => void): HTMLButtonElement {
  const button = el('button', className, label);
  button.type = 'button';
  button.addEventListener('click', onPress);
  return button;
}

/**
 * The mode's own facts, plus one chip when it cannot be played by touch. Only the exception is
 * marked: a chip that every card carries is a chip nobody reads.
 */
function factList(mode: GameMode): HTMLElement {
  const facts = el('span', 'mode-facts');
  for (const fact of mode.facts) {
    const chip = el('span', 'mode-fact');
    chip.append(el('span', 'mode-fact-label', fact.label), el('span', '', fact.value));
    facts.append(chip);
  }
  if (!mode.controls.includes('touch')) facts.append(el('span', 'mode-fact', MODE_MOUSE_ONLY));
  return facts;
}
